import { FieldWrapper } from '@components/shared/forms';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@viewthespace/components';
import {
  TextSize,
  textSizes,
} from '@viewthespace/components/src/lib/rich-text-editor/TextSizeUtils';
import { FloatingPortal, autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import cn from 'classnames';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  name: string;
  labelText: string;
  defaultTextSize?: TextSize;
  containerClass?: string;
};

export const getTextSizeFromClassName = (className?: string): TextSize | null =>
  (Object.keys(textSizes).find(
    textSize => textSizes[textSize as TextSize].className === className,
  ) as TextSize) || null;

const TextSizeDropdown = ({
  name,
  labelText,
  defaultTextSize = 'body18',
  containerClass,
}: Props) => {
  const { t } = useTranslation('common');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, _meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const currentTextSize = getTextSizeFromClassName(field.value) || defaultTextSize;

  const { x, y, refs } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: 'fixed',
    placement: 'top',
    middleware: [offset({ mainAxis: 8, crossAxis: 42 }), flip()],
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        refs.floating.current &&
        !refs.floating.current.contains(event.target as Node) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, refs.floating]);

  return (
    <>
      <FieldWrapper {...{ name, labelText, containerClass }}>
        <button
          type="button"
          className="flex px-1 pb-1 pt-[10px] gap-1 justify-between p-1 border border-solid border-black-020 rounded-[3px] w-full bg-background-primary focus:bg-background-primary !text-[14px] !leading-[22px] h-full"
          onClick={e => {
            e.preventDefault();
            setIsOpen(open => !open);
          }}
          ref={refs.setReference}
        >
          <span>{t(`textSize.${currentTextSize}`)}</span>
          {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </button>
      </FieldWrapper>
      <FloatingPortal root={document.body}>
        {isOpen && (
          <div
            className="w-[410px] h-[330px] overflow-y-scroll overflow-x-hidden rounded drop-shadow dark:drop-shadow-dark bg-background-primary dark:bg-background-tertiary-darkmode dark:border dark:border-border-subdued-darkmode focus:outline-2 ml-[-20px] absolute z-[100]"
            style={{ top: y ?? '', left: x ?? '' }}
            ref={refs.setFloating}
          >
            <ul className="flex flex-col bg-general-light-primary border-general-gray-tertiary w-[410px] rounded">
              {Object.entries(textSizes).map(([textSize, { styles, className }]) => (
                <li
                  role="presentation"
                  key={textSize}
                  className={cn(
                    'flex items-baseline p-1 w-full hover:bg-cta-secondary-hover focus:bg-cta-secondary-pressed cursor-pointer',
                    styles,
                  )}
                  onClick={() => {
                    setIsOpen(false);
                    helpers.setValue(className);
                    helpers.setTouched(true);
                  }}
                >
                  <span>{t(`textSize.${textSize}`)}</span>
                  {textSize === defaultTextSize && (
                    <span className="text-[14px] ml-0.5 text-text-subdued font-normal self-center leading-[16px]">
                      {t('textSize.default')}
                    </span>
                  )}
                  {textSize === currentTextSize && (
                    <span className="ml-auto mr-1 self-center">
                      <CheckIcon size="sm" color="text-success" className="flex" />
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default TextSizeDropdown;
